import { ResultSet, TCubeDimension, TimeDimension, TimeDimensionGranularity } from '@cubejs-client/core';
import { ApexOptions } from 'apexcharts';
import { isNil, isString, merge, range } from 'lodash';
import moment, { Moment } from 'moment';
import { ChartConfigVersion, IChartConfig, IChartResult } from '.';
import { granularityDateFormater } from './dateFormatter';
import { IChartOptions } from './types';

export interface IAxisesAndLegendsResult {
	type: 'IAxisesAndLegendsResult_v1',
	input: {
		options: IChartOptions,
		resultSet: ResultSet
	};
	output: {
		pivots: IPivotContainer,
		chartResult: IChartResult
	};
}

interface IPivotSerie {
	data: number[]
	/**
	 * used by apex chart, must contains the translated value
	 *
	 * @type {string}
	 * @memberof IPivotSerie
	 */
	name: string
	nameCode: string
	measure: string
}
interface IPivotContainer {
	axisNames: Record<string, {
		axisName: string
		index: number
		timeDimensionValue: Moment,
		values: string[] | number[]
	}>
	series: IPivotSerie[],
	seriesCodes: Record<string, {
		/**
		 * measure code + legends values
		 *
		 * @type {string}
		 */
		nameCode: string
		index: number
		timeDimensionValue: Moment
		values: string[] | number[]
		/**
		 * measure code
		 *
		 * @type {string}
		 */
		measure: string
		/**
		 * translation of measure code
		 *
		 * @type {string}
		 */
		measureLocale: string
	}>
}

interface IRawData extends Record<string, any> {
	axisName?: string | null
}

const noAxisDimensionSelected = '--no-axis-dimension-selected--';

export function axisesAndLegends(resultSet: ResultSet, options: IChartOptions, isGroupedByWeek: boolean, preloadedAxisNames: string[] | null = null): IAxisesAndLegendsResult {
	// implement reasoning axeses and legends
	const opt = options;
	const configs = opt.config;

	// https://cube.dev/docs/@cubejs-client-core#result-set-chart-pivot

	const measures = Array.isArray(configs) ? configs[0].measures : configs.measures;
	const legendDimensions = Array.isArray(configs) ? configs[0].legendDimensions : configs.legendDimensions;
	const rs: ResultSet = !isNil(resultSet[0]) ? resultSet[0] : resultSet;
	const timeDimension = retrieveTimeDimension(rs);
	const isTimeDimensionAvailable = isTimeDimension(timeDimension);
	let granularity: TimeDimensionGranularity = 'day';
	if (isTimeDimensionAvailable)
		granularity = (isGroupedByWeek ? 'week' : timeDimension!.granularity!);

	// const axisDimensions = configs.axisDimensions;
	const axisDimensions = Array.isArray(configs) ? getAxisDimension(rs, configs[0]) : getAxisDimension(rs, configs);

	const momentDefault = moment();
	const pivots: IPivotContainer = {
		axisNames: {},
		series: [], // series cardinality = ld-1 values count * ld-2 values count * ... * ld-n values count
		seriesCodes: {}
	};

	const data: IRawData[] = rs.rawData();
	let axisIndex = 0;
	data.forEach(d => {
		let timeDimensionValue = momentDefault;
		let axisValues = [options.translate('charts.detail.defaultAxisValue')];
		if (axisDimensions.length > 1 || axisDimensions[0] !== noAxisDimensionSelected) {
			axisValues = (axisDimensions as any[])
				.map((ad: TCubeDimension | string) => {
					let adCode = ad;
					let adType: string | null = null;
					if (!isString(adCode)) {
						adType = (ad as TCubeDimension).type;
						adCode = adCode.name;
					}
					let value = d[adCode];
					if (!isNil(value) && isTimeDimensionAvailable && adCode === timeDimension!.dimension) {
						// really bad thing, but needed to avoid a double loop over axisDimensions. (.map should not be use to generate side effets!!)
						timeDimensionValue = moment(value); // this should work because there is not more than 1 timeDimension selected at a time
						value = granularityDateFormater(value, granularity, opt.languageCode);
					}
					
					value = translateBooleanValue(value);
					
					return value;
				});
			// .filter(v => !isNil(v));
		}
		const axisName = axisValues.join(options.chartType === 'pie' ? ', ' : '|');
		if (axisValues.some(sv => isNil(sv)) || axisName.trim() === '')
			return;
		if (pivots.axisNames[axisName] === undefined) {
			pivots.axisNames[axisName] = {
				axisName,
				index: axisIndex,
				timeDimensionValue,
				values: axisValues
			};
			axisIndex++;
		}
		d.axisName = axisName;
	});
	const serieLength = axisIndex;

	data.forEach(d => {
		measures.forEach(m => {
			const measureCode = m.name;
			const measureLocale = opt.translate(measureCode);

			let serieName = measureLocale;
			let serieCode = measureCode;
			let serieValues = [measureCode];
			let timeDimensionValue = momentDefault;
			if (legendDimensions.length) {
				serieValues = legendDimensions.map(ld => {
					let valueKey = ld.name;
					if (isTimeDimensionAvailable && ld.name === timeDimension!.dimension) {
						valueKey = `${valueKey}.${timeDimension!.granularity}`;
					}
					let value = d[valueKey];
					if (!isNil(value) && isTimeDimensionAvailable && ld.name === timeDimension!.dimension) {
						// really bad thing, but needed to avoid a double loop over axisDimensions. (.map should be use to generate side effets!!)
						timeDimensionValue = moment(value); // this should work because there is not more than 1 timeDimension selected at a time
						value = granularityDateFormater(value, granularity, opt.languageCode);
					}
					
					value = translateBooleanValue(value);

					return value;
				});
				serieCode = serieValues.join('|');
				if (serieCode.trim() !== '')
					serieCode = `${measureCode}|${serieCode}`;
				serieName = serieValues.join(', ');

				serieName = `${measureLocale}, ${serieName}`;

			}
			if (serieValues.some(sv => isNil(sv)) || serieCode.trim() === '')
				return;
			if (pivots.seriesCodes[serieCode] === undefined) {
				pivots.seriesCodes[serieCode] = {
					index: pivots.series.length,
					timeDimensionValue,
					values: serieValues,
					measure: measureCode,
					measureLocale,
					nameCode: serieCode
				};
			}
			const sIndex = pivots.seriesCodes[serieCode].index;
			const seriesCount = pivots.series.length;
			const serie: IPivotSerie = pivots.series[sIndex] || {
				name: serieName,
				data: range(0, serieLength >= (preloadedAxisNames ?? []).length ? serieLength : (preloadedAxisNames ?? []).length).map(() => 0), // series data cardinality = ad-1 values count * ad-2 values count * ... * ad-n values count
				measure: measureCode,
				nameCode: serieCode,

			};
			if (sIndex === seriesCount) {
				pivots.series.push(serie);
			}

			const axisName = d.axisName;
			if (!isString(axisName) || axisName.trim() === '')
				return;

			const aIndex = isNil(preloadedAxisNames) ? pivots.axisNames[axisName].index : preloadedAxisNames.indexOf(axisName);
			let datum = serie.data[aIndex] || 0;
			if (aIndex === serie.data.length) {
				serie.data.push(datum);
			}
			datum = datum + (d[measureCode] || 0); //parseInt(d[m], 10);
			serie.data[aIndex] = datum;
		});
	});

	// const clonedPivots = cloneDeep(pivots);
	const tempAxisNames: string[] = Array(serieLength).fill('');
	Object
		.values(pivots.axisNames)
		.forEach(an => {
			tempAxisNames[an.index] = an.axisName;
		});
	const chartOpt = merge({},
		opt.apexOptions, {
			xaxis: {
				categories: tempAxisNames.map(v => v.replaceAll('|', ', '))
			}
		} as ApexOptions);
	return {
		type: 'IAxisesAndLegendsResult_v1',
		input: {
			options,
			resultSet: rs
		},
		output: {
			pivots,
			chartResult: {
				options: chartOpt,
				series: pivots.series
			}
		}
	};

	function translateBooleanValue(value: any) {
		if (isNil(value)) {
			return value;
		}

		if (value === true){
			return options.translate('app.true');
		} else if (value === false){
			return options.translate('app.false');
		}

		return value;
	}
}

function isTimeDimension(timeDimension: TimeDimension | null): timeDimension is TimeDimension {
	return timeDimension !== null;
}

function retrieveTimeDimension(resultSet: ResultSet): TimeDimension | null {
	const rs = !isNil(resultSet[0]) ? resultSet[0] : resultSet;
	if (isNil(rs))
		return null;

	const query = rs.query();

	if (query.timeDimensions && query.timeDimensions.length)
		return query.timeDimensions![0];
	return null;
}

function getAxisDimension(resultSet: ResultSet, config: IChartConfig): TCubeDimension[] | string[] {
	const rs = !isNil(resultSet[0]) ? resultSet[0] : resultSet;
	let axisDimensions = config.axisDimensions;
	const legendDimensions = new Set(config.legendDimensions.map(ld => ld.name));
	const configVersion = config.version || -1;
	if (configVersion >= ChartConfigVersion.AxisAndLegends) {
		if (axisDimensions.length === 0)
			return [noAxisDimensionSelected];
		return axisDimensions;
	}
	if (!Array.isArray(axisDimensions) || axisDimensions.length === 0) {
		const dimensions = [...config.selectedDimensions];
		const timeDimension = retrieveTimeDimension(rs);
		if (isTimeDimension(timeDimension) && !legendDimensions.has(timeDimension.dimension)) {
			return [timeDimension.dimension];
		}
		else if (Array.isArray(dimensions) && dimensions.length > 0) {
			axisDimensions = dimensions.filter(d => !legendDimensions.has(d.name));
		}
		axisDimensions = axisDimensions || [];
		if (axisDimensions.length === 0) {
			return [noAxisDimensionSelected];
		}
	}
	return axisDimensions;
}
